<template>
  <div class="min-h-screen flex flex-col bg-light-background dark:bg-dark-background">
    
    <!-- Header -->
    <header class="flex items-center justify-between p-4 lg:p-6">
      <router-link to="/" class="flex items-center gap-2">
        <div class="w-10 h-10 bg-light-accent rounded-xl flex items-center justify-center">
          <CalendarDaysIcon class="w-6 h-6 text-white" />
        </div>
        <span class="text-xl font-bold text-light-text-primary dark:text-dark-text-primary">
          Gestor<span class="text-light-accent">Citas</span>
        </span>
      </router-link>
      
      <button 
        @click="toggleTheme"
        class="w-10 h-10 rounded-full bg-light-border/50 dark:bg-dark-border/50 flex items-center justify-center hover:bg-light-border dark:hover:bg-dark-border transition-colors"
      >
        <SunIcon v-if="isDark" class="w-5 h-5 text-dark-text-primary" />
        <MoonIcon v-else class="w-5 h-5 text-light-text-primary" />
      </button>
    </header>
    
    <!-- Contenido Principal -->
    <main class="flex-1 flex flex-col items-center justify-center px-6 py-8">
      <div class="max-w-md w-full text-center">
        
        <!-- Ilustración del Calendario -->
        <div class="relative mb-8 flex justify-center">
          <!-- Número de fondo -->
          <span class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10rem] font-black text-light-accent/5 dark:text-dark-accent/10 leading-none select-none pointer-events-none">
            {{ errorCode }}
          </span>
          
          <!-- Decoraciones flotantes -->
          <div class="absolute -top-2 left-8 animate-float-slow z-10">
            <div class="bg-white dark:bg-dark-background-modal shadow-lg rounded-full px-3 py-1.5 flex items-center gap-2">
              <div class="w-2 h-2 bg-green-400 rounded-full"></div>
              <span class="text-xs font-medium text-light-text-primary dark:text-dark-text-primary">09:00 AM</span>
            </div>
          </div>
          
          <div class="absolute bottom-4 right-4 animate-float-delayed z-10">
            <div class="bg-white dark:bg-dark-background-modal shadow-lg rounded-full px-3 py-1.5 flex items-center gap-2">
              <ClockIcon class="w-3 h-3 text-light-accent" />
              <span class="text-xs font-medium text-light-text-primary dark:text-dark-text-primary">??:??</span>
            </div>
          </div>
          
          <!-- Calendario SVG -->
          <div class="relative z-0 w-52 h-52">
            <svg viewBox="0 0 200 200" class="w-full h-full drop-shadow-xl">
              <ellipse cx="100" cy="185" rx="60" ry="8" class="fill-light-accent/10 dark:fill-dark-accent/15" />
              <rect x="35" y="45" width="130" height="120" rx="12" class="fill-white dark:fill-dark-background-modal" />
              <rect x="35" y="45" width="130" height="30" rx="12" class="fill-light-accent" />
              <rect x="35" y="60" width="130" height="15" class="fill-light-accent" />
              <rect x="55" y="38" width="8" height="18" rx="2" class="fill-light-text-primary dark:fill-dark-text-primary" />
              <rect x="137" y="38" width="8" height="18" rx="2" class="fill-light-text-primary dark:fill-dark-text-primary" />
              <text x="100" y="62" text-anchor="middle" fill="white" style="font-size: 9px; font-weight: 600;">ERROR {{ errorCode }}</text>
              <g>
                <rect x="48" y="85" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="73" y="85" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="98" y="85" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="123" y="85" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="48" y="110" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="73" y="110" width="45" height="20" rx="4" class="fill-light-accent/20 dark:fill-dark-accent/25" />
                <circle cx="85" cy="120" r="2.5" class="fill-light-text-primary dark:fill-dark-text-primary" />
                <circle cx="105" cy="120" r="2.5" class="fill-light-text-primary dark:fill-dark-text-primary" />
                <rect x="123" y="110" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="48" y="135" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="73" y="135" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="98" y="135" width="20" height="20" rx="4" class="fill-light-accent/10 dark:fill-dark-accent/15" />
                <rect x="123" y="135" width="20" height="20" rx="4" class="fill-light-accent" />
                <text x="133" y="149" text-anchor="middle" fill="white" style="font-size: 10px; font-weight: bold;">?</text>
              </g>
            </svg>
          </div>
        </div>
        
        <!-- Textos -->
        <h1 class="text-2xl sm:text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-3">
          {{ errorTitle }}
        </h1>
        <p class="text-light-text-secondary dark:text-dark-text-secondary mb-8">
          {{ errorDescription }}
        </p>
        
        <!-- Botones de Navegación -->
        <div class="space-y-3 mb-8">
          <router-link
            v-if="!isAppError"
            to="/"
            class="w-full flex items-center justify-center gap-2 px-6 py-4 bg-light-accent hover:bg-light-accent/90 text-white rounded-xl font-semibold transition-all shadow-lg shadow-light-accent/20"
          >
            <CalendarDaysIcon class="w-5 h-5" />
            {{ t('common.errorPage.goToSiteCta') }}
          </router-link>

          <router-link
            v-if="isAuthenticated"
            to="/dashboard"
            class="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold transition-all"
            :class="isAppError
              ? 'bg-light-accent hover:bg-light-accent/90 text-white shadow-lg shadow-light-accent/20'
              : 'border-2 border-light-border dark:border-dark-border hover:border-light-accent text-light-text-primary dark:text-dark-text-primary'"
          >
            <Squares2X2Icon class="w-5 h-5" />
            {{ t('common.errorPage.goToDashboardCta') }}
          </router-link>
        </div>
        
        <!-- Botón para juego -->
        <button
          @click="showMiniGame = true"
          class="inline-flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-accent transition-colors"
        >
          <CalendarDaysIcon class="w-4 h-4" />
          ¿Aburrido? Atrapa citas mientras esperas
        </button>
        
      </div>
    </main>
    
    <!-- Footer -->
    <footer class="p-4 text-center">
      <p class="text-xs text-light-text-secondary dark:text-dark-text-secondary">
        © 2024 GIOR · 
        <a href="#" class="hover:text-light-accent transition-colors">Soporte</a> · 
        <a href="#" class="hover:text-light-accent transition-colors">Dashboard</a>
      </p>
    </footer>
    
    <!-- Modal de Mini Juegos -->
    <Teleport to="body">
      <Transition name="fade">
        <div 
          v-if="showMiniGame" 
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          @click.self="showMiniGame = false"
        >
          <div class="w-full max-w-sm">
            <MiniGame @close="showMiniGame = false" />
          </div>
        </div>
      </Transition>
    </Teleport>
    
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useTheme } from '@/composables/useTheme'
import {
  CalendarDaysIcon,
  ClockIcon,
  Squares2X2Icon,
  SunIcon,
  MoonIcon
} from '@heroicons/vue/24/outline'

const MiniGame = defineAsyncComponent(() => import('@/components/MiniGame/MiniGame.vue'))

const props = defineProps({
  type: { type: String, default: '404' },
  message: { type: String, default: '' },
  source: { type: String, default: '' }
})

const route = useRoute()
const authStore = useAuthStore()
const { isDark, toggleTheme } = useTheme()
const { t } = useI18n()

const showMiniGame = ref(false)

const isAuthenticated = computed(() => authStore.isAuthenticated)
const errorType = computed(() => route.query.type || props.type || '404')

// `source` llega por query (push desde el errorHandler/timeout de main.js) o por prop (ruta
// catch-all de 404, ver main.js). Sin ninguno de los dos (ej. navegación manual a /error),
// se infiere por el path: todo lo que vive bajo /dashboard es la app autenticada.
const errorSource = computed(() => {
  const explicit = String(route.query.source || props.source || '')
  if (explicit === 'app' || explicit === 'site') return explicit
  return route.path.startsWith('/dashboard') ? 'app' : 'site'
})
// Error venido de la app autenticada: un solo CTA ("Ir al dashboard"), no tiene sentido
// ofrecer "Ir al sitio" a alguien que ya estaba en el panel.
const isAppError = computed(() => errorSource.value === 'app' && isAuthenticated.value)

const errorConfig = computed(() => {
  const configs = {
    '404': {
      code: '404',
      title: '¡Parece que esta página se tomó el día libre!',
      description: 'Buscamos por todo el calendario, pero no logramos encontrar lo que buscabas.'
    },
    'error': {
      code: '500',
      title: '¡Ups! Algo salió mal',
      description: 'Hubo un problema técnico. Nuestro equipo ya está trabajando en ello.'
    },
    'timeout': {
      code: '408',
      title: '¡Tiempo de espera agotado!',
      description: 'La página tardó demasiado en cargar. Verifica tu conexión.'
    }
  }
  return configs[errorType.value] || configs['404']
})

const errorCode = computed(() => errorConfig.value.code)
const errorTitle = computed(() => props.message || errorConfig.value.title)
const errorDescription = computed(() => errorConfig.value.description)
</script>

<style scoped>
@keyframes float-slow {
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50% { transform: translateY(-8px) rotate(2deg); }
}

@keyframes float-delayed {
  0%, 100% { transform: translateY(0px) rotate(2deg); }
  50% { transform: translateY(-6px) rotate(-2deg); }
}

.animate-float-slow {
  animation: float-slow 3s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 3s ease-in-out infinite;
  animation-delay: 0.5s;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
